import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Receipt, Plus, Pencil, Trash, Star } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvoiceTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ template_name: '', header_text: '', footer_text: '', show_logo: true, show_gst: true, show_hsn: true, terms_and_conditions: '', bank_details: '', custom_fields: [] });

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get(`${API}/api/seller/invoice-templates`, { withCredentials: true });
      setTemplates(data.templates || []);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!form.template_name) { toast.error('Template name required'); return; }
    try {
      if (editing) {
        await axios.patch(`${API}/api/seller/invoice-templates/${editing.template_id}`, form, { withCredentials: true });
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/api/seller/invoice-templates`, form, { withCredentials: true });
        toast.success('Template created');
      }
      setDialogOpen(false);
      setEditing(null);
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const handleDelete = async (tid) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/api/seller/invoice-templates/${tid}`, { withCredentials: true });
      toast.success('Template deleted');
      fetchTemplates();
    } catch { toast.error('Failed to delete'); }
  };

  const setDefault = async (tid) => {
    try {
      await axios.patch(`${API}/api/seller/invoice-templates/${tid}`, { is_default: true }, { withCredentials: true });
      toast.success('Default template set');
      fetchTemplates();
    } catch { toast.error('Failed to update'); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-5xl" data-testid="invoice-templates-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#0A0A0A]">Invoice Templates</h1>
            <p className="text-sm text-[#6b6b8a] mt-1">Custom invoice designs for your orders</p>
          </div>
          <Button data-testid="add-template-btn" onClick={() => { setEditing(null); setForm({ template_name: '', header_text: '', footer_text: '', show_logo: true, show_gst: true, show_hsn: true, terms_and_conditions: '', bank_details: '', custom_fields: [] }); setDialogOpen(true); }} className="bg-[#7d64ff] hover:bg-[#6b4fef]">
            <Plus size={16} className="mr-2" /> New Template
          </Button>
        </div>

        {loading ? <div className="text-center py-12 text-[#9c9db7]">Loading...</div> : templates.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-[#e5e5ea]">
            <Receipt size={48} className="mx-auto text-[#9c9db7] mb-3" />
            <p className="text-[#6b6b8a]">No invoice templates yet.</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {templates.map(t => (
              <div key={t.template_id} className={`bg-white rounded-xl border p-5 ${t.is_default ? 'border-[#7d64ff] ring-1 ring-[#7d64ff]/20' : 'border-[#e5e5ea]'}`} data-testid={`template-${t.template_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A]">{t.template_name}</h3>
                    {t.is_default && <span className="inline-flex items-center gap-1 text-[10px] font-medium text-[#7d64ff]"><Star size={10} weight="fill" /> Default</span>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(t); setForm({ template_name: t.template_name, header_text: t.header_text, footer_text: t.footer_text, show_logo: t.show_logo, show_gst: t.show_gst, show_hsn: t.show_hsn, terms_and_conditions: t.terms_and_conditions, bank_details: t.bank_details, custom_fields: t.custom_fields || [] }); setDialogOpen(true); }}><Pencil size={14} /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(t.template_id)} className="text-red-500"><Trash size={14} /></Button>
                  </div>
                </div>
                <div className="space-y-1 text-xs text-[#6b6b8a]">
                  {t.header_text && <p>Header: {t.header_text.substring(0, 40)}...</p>}
                  <div className="flex gap-2">
                    {t.show_logo && <span className="px-1.5 py-0.5 bg-gray-100 rounded">Logo</span>}
                    {t.show_gst && <span className="px-1.5 py-0.5 bg-gray-100 rounded">GST</span>}
                    {t.show_hsn && <span className="px-1.5 py-0.5 bg-gray-100 rounded">HSN</span>}
                  </div>
                </div>
                {!t.is_default && <Button variant="outline" size="sm" onClick={() => setDefault(t.template_id)} className="mt-3 text-xs w-full">Set as Default</Button>}
              </div>
            ))}
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto" data-testid="template-dialog">
            <DialogHeader><DialogTitle>{editing ? 'Edit Template' : 'New Invoice Template'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Template Name</Label><Input data-testid="template-name" value={form.template_name} onChange={e => setForm(f => ({ ...f, template_name: e.target.value }))} placeholder="Standard Invoice" /></div>
              <div><Label>Header Text</Label><textarea value={form.header_text} onChange={e => setForm(f => ({ ...f, header_text: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm min-h-[60px]" placeholder="Company header text" /></div>
              <div><Label>Footer Text</Label><Input value={form.footer_text} onChange={e => setForm(f => ({ ...f, footer_text: e.target.value }))} placeholder="Thank you for your business" /></div>
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={form.show_logo} onChange={e => setForm(f => ({ ...f, show_logo: e.target.checked }))} className="rounded" /> Logo</label>
                <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={form.show_gst} onChange={e => setForm(f => ({ ...f, show_gst: e.target.checked }))} className="rounded" /> GST</label>
                <label className="flex items-center gap-1.5 text-sm"><input type="checkbox" checked={form.show_hsn} onChange={e => setForm(f => ({ ...f, show_hsn: e.target.checked }))} className="rounded" /> HSN</label>
              </div>
              <div><Label>Terms & Conditions</Label><textarea value={form.terms_and_conditions} onChange={e => setForm(f => ({ ...f, terms_and_conditions: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm min-h-[60px]" /></div>
              <div><Label>Bank Details</Label><textarea value={form.bank_details} onChange={e => setForm(f => ({ ...f, bank_details: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm min-h-[60px]" placeholder="Account: ..., IFSC: ..." /></div>
              <Button data-testid="save-template-btn" onClick={handleSave} className="w-full bg-[#7d64ff] hover:bg-[#6b4fef]">{editing ? 'Update' : 'Create'} Template</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
